import {
  calculateTerritory,
  type GreatKingdomAction,
  type GreatKingdomCell,
  type GreatKingdomPlayerId,
  type GreatKingdomPosition,
  type GreatKingdomState,
  getAttackWarnings,
  validateAction,
} from "@cloudnest/great-kingdom-engine";
import { useEffect, useMemo, useRef } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  greatKingdomMultiplayerActions,
  useGreatKingdomMultiplayer,
} from "./multiplayer-game-store";
import "./styles/great-kingdom-tokens.css";

const FILE_LABELS = ["A", "B", "C", "D", "E", "F", "G", "H", "I"] as const;
const SEAT_ORDER: readonly GreatKingdomPlayerId[] = ["p1", "p2"];

type SeatUserMap = Readonly<Record<GreatKingdomPlayerId, string | null>>;

function buildSeatUserMap(participantUserIds: readonly string[]): SeatUserMap {
  return {
    p1: participantUserIds[0] ?? null,
    p2: participantUserIds[1] ?? null,
  };
}

function toCoord(row: number, col: number): string {
  return `${FILE_LABELS[col] ?? "?"}${row + 1}`;
}

function getPlayerName(
  seatId: GreatKingdomPlayerId,
  seatUsers: SeatUserMap,
  currentRoom: ReturnType<typeof useLobby>["currentRoom"],
): string {
  const userId = seatUsers[seatId];
  if (!currentRoom || !userId) return seatId === "p1" ? "Blue" : "Orange";
  return getParticipantDisplayName(
    currentRoom,
    userId,
    seatId === "p1" ? "Blue" : "Orange",
  );
}

function getMySeatId(
  userId: string | undefined,
  seatUsers: SeatUserMap,
): GreatKingdomPlayerId | null {
  if (!userId) return null;
  if (seatUsers.p1 === userId) return "p1";
  if (seatUsers.p2 === userId) return "p2";
  return null;
}

function getCellLabel(cell: GreatKingdomCell): string {
  if (cell === "p1") return "B";
  if (cell === "p2") return "O";
  if (cell === "neutral") return "N";
  return "";
}

function getPhaseText(state: GreatKingdomState): string {
  if (state.phase === "neutral-setup") return "중립 성 배치";
  if (state.phase === "game-over") {
    return state.endReason === "siege" ? "포위 승리" : "영토 판정";
  }
  return "성 배치";
}

function getActionForCell(
  state: GreatKingdomState,
  playerId: GreatKingdomPlayerId,
  row: number,
  col: number,
): GreatKingdomAction {
  if (state.phase === "neutral-setup") {
    return { type: "PlaceNeutral", playerId, row, col };
  }
  return { type: "PlaceCastle", playerId, row, col };
}

function getLastActionPosition(
  state: GreatKingdomState,
): GreatKingdomPosition | null {
  const action = state.lastAction;
  if (!action) return null;
  if (action.type === "Pass") return null;
  return { row: action.row, col: action.col };
}

function BoardCell({
  cell,
  isLast,
  isOpponentTerritory,
  isPlayable,
  onClick,
  row,
  col,
}: {
  readonly cell: GreatKingdomCell;
  readonly isLast: boolean;
  readonly isOpponentTerritory: boolean;
  readonly isPlayable: boolean;
  readonly onClick: () => void;
  readonly row: number;
  readonly col: number;
}) {
  const coord = toCoord(row, col);
  const classes = [
    "gk-cell",
    cell !== "empty" ? `has-${cell}` : "empty",
    isPlayable ? "playable" : "",
    isLast ? "last" : "",
    isOpponentTerritory ? "blocked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={classes}
      data-testid={`great-kingdom-cell-${coord}`}
      data-cell={coord}
      data-row={row}
      data-col={col}
      data-coord={coord}
      data-cell-state={cell}
      data-legal={isPlayable ? "true" : "false"}
      disabled={!isPlayable}
      aria-label={`${coord} ${cell}`}
      onClick={onClick}
    >
      <span className="gk-cell-piece" aria-hidden="true">
        {getCellLabel(cell)}
      </span>
    </button>
  );
}

function KingdomBoard({
  mySeatId,
  state,
}: {
  readonly mySeatId: GreatKingdomPlayerId | null;
  readonly state: GreatKingdomState;
}) {
  const lastPosition = getLastActionPosition(state);
  const territory = calculateTerritory(state.board);

  function handleCell(row: number, col: number): void {
    if (!mySeatId) return;
    const action = getActionForCell(state, mySeatId, row, col);
    void greatKingdomMultiplayerActions.submitAction(action);
  }

  return (
    <section className="gk-board-wrap" aria-label="그레이트 킹덤 보드">
      <div className="gk-file-coords" aria-hidden="true">
        {FILE_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="gk-board-row">
        <div className="gk-rank-coords" aria-hidden="true">
          {state.board.map((_, row) => (
            <span key={row}>{row + 1}</span>
          ))}
        </div>
        <div className="gk-board" data-testid="great-kingdom-board">
          {state.board.map((line, row) =>
            line.map((cell, col) => {
              const action =
                mySeatId === null
                  ? null
                  : getActionForCell(state, mySeatId, row, col);
              const validation = action ? validateAction(state, action) : null;
              const cellOwner = territory.cellOwners[`${row},${col}`] ?? null;
              return (
                <BoardCell
                  key={`${row}-${col}`}
                  cell={cell}
                  col={col}
                  row={row}
                  isLast={lastPosition?.row === row && lastPosition.col === col}
                  isOpponentTerritory={
                    mySeatId !== null &&
                    cell === "empty" &&
                    cellOwner !== null &&
                    cellOwner !== mySeatId
                  }
                  isPlayable={validation?.valid === true}
                  onClick={() => handleCell(row, col)}
                />
              );
            }),
          )}
        </div>
      </div>
    </section>
  );
}

function PlayerPanel({
  mySeatId,
  seatUsers,
  state,
}: {
  readonly mySeatId: GreatKingdomPlayerId | null;
  readonly seatUsers: SeatUserMap;
  readonly state: GreatKingdomState;
}) {
  const { currentRoom } = useLobby();

  return (
    <section className="gk-player-panel" data-testid="great-kingdom-players">
      {SEAT_ORDER.map((seatId) => {
        const player = state.players.find(
          (candidate) => candidate.playerId === seatId,
        );
        const active =
          state.currentPlayerId === seatId && state.phase !== "game-over";
        return (
          <article
            className={`gk-player-card ${seatId} ${active ? "active" : ""}`}
            data-player-id={seatId}
            data-role="player"
            data-seat={seatId}
            data-current={active ? "true" : "false"}
            data-testid={`great-kingdom-player-${seatId}`}
            key={seatId}
          >
            <span className="gk-player-stone" aria-hidden="true" />
            <div>
              <strong>
                {mySeatId === seatId ? "나 · " : ""}
                {getPlayerName(seatId, seatUsers, currentRoom)}
              </strong>
              <span data-testid={`great-kingdom-score-${seatId}`}>
                예상 {player?.scoreEstimate ?? state.territory[seatId]}
              </span>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function StatusPanel({
  mySeatId,
  seatUsers,
  state,
}: {
  readonly mySeatId: GreatKingdomPlayerId | null;
  readonly seatUsers: SeatUserMap;
  readonly state: GreatKingdomState;
}) {
  const { currentRoom } = useLobby();
  const activeName = getPlayerName(
    state.currentPlayerId,
    seatUsers,
    currentRoom,
  );
  const isMyTurn = mySeatId === state.currentPlayerId;
  const passAction: GreatKingdomAction | null =
    mySeatId === null ? null : { type: "Pass", playerId: mySeatId };
  const passValidation = passAction ? validateAction(state, passAction) : null;

  return (
    <aside className="gk-status-panel">
      <div className="gk-status-block">
        <span>{getPhaseText(state)}</span>
        <strong data-testid="great-kingdom-active-player">
          {state.phase === "game-over"
            ? "게임 종료"
            : isMyTurn
              ? "내 차례"
              : `${activeName} 차례`}
        </strong>
      </div>

      {state.phase === "game-over" ? (
        <div className="gk-game-over" data-testid="great-kingdom-game-over">
          <span>{state.endReason === "siege" ? "포위" : "더블 패스"}</span>
          <strong data-testid="great-kingdom-winner">
            승자{" "}
            {state.winnerId
              ? getPlayerName(state.winnerId, seatUsers, currentRoom)
              : "-"}
          </strong>
        </div>
      ) : (
        <button
          type="button"
          className="gk-pass-button"
          data-testid="great-kingdom-pass"
          disabled={passValidation?.valid !== true}
          onClick={() => {
            if (passAction) {
              void greatKingdomMultiplayerActions.submitAction(passAction);
            }
          }}
        >
          패스
        </button>
      )}
    </aside>
  );
}

function WarningPanel({
  mySeatId,
  state,
}: {
  readonly mySeatId: GreatKingdomPlayerId | null;
  readonly state: GreatKingdomState;
}) {
  const warningPlayer = mySeatId ?? state.currentPlayerId;
  const warnings = getAttackWarnings(state, warningPlayer);

  return (
    <section className="gk-warning-panel" data-testid="great-kingdom-warnings">
      <strong>포위 경고</strong>
      {warnings.length === 0 ? (
        <span>위험한 적 성 없음</span>
      ) : (
        <ul>
          {warnings.map((warning) => (
            <li
              key={`${warning.owner}-${warning.liberty.row}-${warning.liberty.col}`}
            >
              {warning.owner.toUpperCase()} 숨구멍{" "}
              {toCoord(warning.liberty.row, warning.liberty.col)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, lastError, mySeatId } = useGreatKingdomMultiplayer();
  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const participantUserIds = useMemo(
    () => currentRoom?.participantUserIds ?? [],
    [currentRoom?.participantUserIds],
  );
  const seatUsers = useMemo(
    () => buildSeatUserMap(participantUserIds),
    [participantUserIds],
  );
  const effectiveMySeatId = mySeatId ?? getMySeatId(user?.id, seatUsers);
  const actionsRef = useRef(greatKingdomMultiplayerActions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = greatKingdomMultiplayerActions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialize at concrete room/user role boundaries.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    if ((currentRoom.participantUserIds ?? []).length < 2) return;

    if (isHost) {
      void greatKingdomMultiplayerActions.initializeAsHost(
        roomId,
        currentRoom.participantUserIds ?? [],
        currentRoom.config,
      );
    } else {
      greatKingdomMultiplayerActions.initializeAsGuest(
        roomId,
        currentRoom.participantUserIds ?? [],
      );
      void greatKingdomMultiplayerActions.replayEvents(roomId);
    }

    return () => {
      greatKingdomMultiplayerActions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost]);

  useEffect(() => {
    if (!roomId) return;
    const intervalId = setInterval(() => {
      void greatKingdomMultiplayerActions.catchUpEvents(roomId);
    }, 4000);
    return () => clearInterval(intervalId);
  }, [roomId]);

  function leaveGame(): void {
    greatKingdomMultiplayerActions.reset();
    void lobbyActions.leaveGame();
    navigateTo("/great-kingdom/lobby");
  }

  if (!currentRoom || !gameState) {
    return (
      <div
        className="gk-screen gk-loading"
        data-game="great-kingdom"
        data-testid="great-kingdom-screen"
      >
        <section className="gk-empty-card">
          <h1>그레이트 킹덤</h1>
          <p>
            {connectionStatus === "connected"
              ? "방장이 보드를 준비 중입니다."
              : "게임 서버에 연결 중입니다."}
          </p>
          {lastError ? <div className="gk-toast">{lastError}</div> : null}
        </section>
      </div>
    );
  }

  const isObserver = effectiveMySeatId === null;

  return (
    <div
      className="gk-screen"
      data-game="great-kingdom"
      data-phase={gameState.phase}
      data-active-player={gameState.currentPlayerId}
      data-current-player={gameState.currentPlayerId}
      data-pass-streak={gameState.passStreak}
      data-winner={gameState.winnerId ?? ""}
      data-end-reason={gameState.endReason ?? ""}
      data-player-id={effectiveMySeatId ?? "observer"}
      data-role={isObserver ? "observer" : "player"}
      data-observer={isObserver ? "true" : "false"}
      data-testid="game-screen"
    >
      <header className="gk-topbar" data-testid="great-kingdom-topbar">
        <div>
          <strong>그레이트 킹덤</strong>
          <span>{connectionStatus}</span>
        </div>
        <div className="gk-top-actions">
          {isObserver ? (
            <span
              className="gk-observer-pill"
              data-testid="great-kingdom-observer-pill"
            >
              관전
            </span>
          ) : null}
          <button type="button" onClick={leaveGame}>
            나가기
          </button>
        </div>
      </header>

      {lastError ? (
        <div className="gk-toast gk-floating-error">
          <span>{lastError}</span>
          <button
            type="button"
            onClick={() => greatKingdomMultiplayerActions.clearError()}
          >
            닫기
          </button>
        </div>
      ) : null}

      <main className="gk-layout">
        <section className="gk-main-stage">
          <KingdomBoard mySeatId={effectiveMySeatId} state={gameState} />
        </section>
        <section className="gk-side-rail">
          <PlayerPanel
            mySeatId={effectiveMySeatId}
            seatUsers={seatUsers}
            state={gameState}
          />
          <StatusPanel
            mySeatId={effectiveMySeatId}
            seatUsers={seatUsers}
            state={gameState}
          />
          <WarningPanel mySeatId={effectiveMySeatId} state={gameState} />
        </section>
      </main>
    </div>
  );
}
